import { useCallback, useEffect, useRef } from "react";
import { clientLogger } from "~/logger.client";
import type { RouterOutputs } from "~/services/offstreetApi/types";
import { ACCEPT_UI_SCRIPT_URLS } from "../../paymentSdkUrls";
import { useScript } from "../useScript";

type AcceptUIResponse = {
	messages?: {
		resultCode?: string;
		message?: Array<{ code?: string; text?: string }>;
	};
	opaqueData?: {
		dataDescriptor?: string;
		dataValue?: string;
	};
	encryptedCardData?: {
		cardNumber?: string;
		expDate?: string;
		bin?: string;
	};
};

type AuthorizeNetCallbacks = {
	onApproval: (nonce: string) => void;
	onError: (message: string) => void;
};

type UseAuthorizeNetProps = {
	paymentSession?: RouterOutputs["paymentSession"]["createPaymentSessionPublic"]["session"];
	callbacks: AuthorizeNetCallbacks;
};

type AcceptUIResponseHandler = (response: AcceptUIResponse) => void;

declare global {
	interface Window {
		acceptUIResponseHandler?: AcceptUIResponseHandler;
	}
}

// AcceptUI.js binds once per load to the first `.AcceptUI` element, so the button lives outside React.
const ACCEPT_UI_BUTTON_ID = "acceptUIButton";
const RESPONSE_HANDLER_NAME = "acceptUIResponseHandler";

type AcceptUIConfig = {
	scriptUrl: string;
	apiLoginId: string;
	clientKey: string;
};

const ensureAcceptUIButton = ({ scriptUrl, apiLoginId, clientKey }: AcceptUIConfig) => {
	const existing = document.getElementById(ACCEPT_UI_BUTTON_ID);
	if (
		existing?.dataset.scripturl === scriptUrl &&
		existing.dataset.apiloginid === apiLoginId &&
		existing.dataset.clientkey === clientKey
	) {
		return;
	}
	existing?.remove();
	for (const url of Object.values(ACCEPT_UI_SCRIPT_URLS)) {
		document.querySelector(`script[src="${url}"]`)?.remove();
	}

	const button = document.createElement("button");
	button.id = ACCEPT_UI_BUTTON_ID;
	button.type = "button";
	button.className = "AcceptUI";
	button.style.display = "none";
	button.dataset.scripturl = scriptUrl;
	button.dataset.apiloginid = apiLoginId;
	button.dataset.clientkey = clientKey;
	button.dataset.billingaddressoptions = '{"show":true,"required":true}';
	button.dataset.paymentoptions = '{"showCreditCard":true,"showBankAccount":false}';
	button.dataset.acceptuiformbtntxt = "Pay";
	button.dataset.acceptuiformheadertxt = "Payment Information";
	button.dataset.responsehandler = RESPONSE_HANDLER_NAME;
	document.body.appendChild(button);
};

export const useAuthorizeNet = ({ paymentSession, callbacks }: UseAuthorizeNetProps) => {
	const callbacksRef = useRef<AuthorizeNetCallbacks>(callbacks);
	callbacksRef.current = callbacks;

	// Dedup guard: AcceptUI nonces are single-use, so the same nonce should never be processed twice
	const lastProcessedNonce = useRef<string | null>(null);

	const hasSession = !!paymentSession;
	const isLive = paymentSession?.client?.isLive ?? false;
	const scriptUrl = isLive ? ACCEPT_UI_SCRIPT_URLS.live : ACCEPT_UI_SCRIPT_URLS.sandbox;
	const apiLoginId = paymentSession?.client?.apiLoginId ?? "";
	const clientKey = paymentSession?.client?.clientKey ?? "";

	useEffect(() => {
		if (!hasSession) {
			return;
		}
		ensureAcceptUIButton({ scriptUrl, apiLoginId, clientKey });
	}, [hasSession, scriptUrl, apiLoginId, clientKey]);

	const isReady = useScript(scriptUrl, {
		enabled: hasSession,
		logGroup: "AUTHORIZE_NET_SCRIPT_LOAD_ERROR",
	});

	useEffect(() => {
		const handler: AcceptUIResponseHandler = (response) => {
			if (response.messages?.resultCode !== "Ok") {
				const errorText = response.messages?.message?.[0]?.text ?? "Payment failed";
				callbacksRef.current.onError(errorText);
				return;
			}

			const nonce = response.opaqueData?.dataValue;
			if (!nonce) {
				clientLogger.error(
					"AcceptUI response missing opaqueData.dataValue",
					new Error("Missing payment nonce"),
					{ logGroup: "AUTHORIZE_NET_MISSING_NONCE" },
				);
				callbacksRef.current.onError("Payment failed");
				return;
			}

			if (lastProcessedNonce.current === nonce) {
				return;
			}
			lastProcessedNonce.current = nonce;

			callbacksRef.current.onApproval(nonce);
		};

		window.acceptUIResponseHandler = handler;

		return () => {
			if (window.acceptUIResponseHandler === handler) {
				window.acceptUIResponseHandler = undefined;
			}
		};
	}, []);

	const openLightbox = useCallback(() => {
		document.getElementById(ACCEPT_UI_BUTTON_ID)?.click();
	}, []);

	return { isReady, openLightbox };
};
